import { Module } from 'vuex';
import { Step } from '../../components/ProcessBar/ProcessBar.models';
import router, { getPublicRoutes } from '../../utils/router';
import { MobileBreakpoints, Breakpoints, MediaQueryType, TabletBreakpoints } from '@/utils/services/mediaQueryManager';
import { RootState } from '..';
import i18n from '../../i18n';
import { Route, RawLocation } from 'vue-router';
import { tRoute } from '@/utils/plugins/i18nRoutes';
import { RouteNames } from '@/utils/router/router.models';

const SET_LOADING_STATE = 'SET_LOADING_STATE';
const SET_INITIALIZED_STATE = 'SET_INITIALIZED_STATE';
const SET_INITIAL_ANIMATION_COMPLETED = 'SET_INITIAL_ANIMATION_COMPLETED';
const SET_DEVICE_MEDIA_QUERY = 'SET_DEVICE_MEDIA_QUERY';
const SET_IMAGE_MEDIA_QUERY = 'SET_IMAGE_MEDIA_QUERY';
const SET_PORTRAIT = 'SET_PORTRAIT';
const SET_STEP_COMPLETE = 'SET_STEP_COMPLETE';
const SET_PROCESS_ROUTES = 'SET_PROCESS_ROUTES';
const SET_FONTS_LOADED = 'SET_FONTS_LOADED';
const SET_CURRENT_ROUTE = 'SET_CURRENT_ROUTE';
const RESET_STATE = 'RESET_STATE';
const SET_LOCALE = 'SET_LOCALE';

export interface AppState {
  basePath: string;
  baseApiPath: string;
  loading: boolean;
  initialized: boolean;
  initialAnimationCompleted: boolean;
  locale: string;
  deviceMQ: string;
  imageMQ: string;
  isPortrait: boolean;
  processRoutes: Step[];
  fontsLoaded: boolean;
  currentRoute?: Route;
}

export interface FloorState {
  floorID: string;
  zoneID?: string;
}

const appInitialState = (): AppState => ({
  basePath: process.env.VUE_APP_BASE_PATH || 'https://baufritz-configurator-dev.eo-systems.net',
  baseApiPath:
    process.env.VUE_APP_API_BASE_PATH || 'https://baufritz-configurator-dev.eo-systems.net/api/configurator/v1',
  loading: true,
  initialized: false,
  initialAnimationCompleted: false,
  locale: i18n.locale,
  deviceMQ: '',
  imageMQ: '',
  isPortrait: false,
  processRoutes: [],
  fontsLoaded: false
});

export const appModule: Module<AppState, RootState> = {
  namespaced: true,
  state: appInitialState(),
  getters: {
    isMobile(state): boolean {
      return state.deviceMQ in MobileBreakpoints;
    },
    isTablet(state): boolean {
      return state.deviceMQ in TabletBreakpoints;
    },
    // TODO: test with split window / multitasking on tablet device
    isTabletPortrait(state): boolean {
      // second condition (deviceMQ) is included so this getter does get updated when the MQ watcher fires
      // since tablets will report their "old" window sizes at first, making the first condition only work properly
      // on desktop devices / devices properly reporting resizing
      // in this case, the second condition will report the matching media query condition correctly IF the first condition does not
      // - this means 768px height, MQ4 will be accepted, and 1024px height, MQ3 will also be accepted.
      return state.isPortrait && (window.outerHeight >= Breakpoints.MQ4 || state.deviceMQ === 'MQ4');
    },
    linkBaseUrl(state): string {
      return `${state.basePath}/${state.locale.split('-')[0]}/`;
    }
  },
  actions: {
    setMediaQuery({ commit }, { MQ, type }: { MQ: string; type: MediaQueryType }) {
      const mutationType = type === MediaQueryType.Device ? SET_DEVICE_MEDIA_QUERY : SET_IMAGE_MEDIA_QUERY;
      commit(mutationType, MQ);
    },
    setPortrait({ commit }, isPortrait: boolean) {
      commit(SET_PORTRAIT, isPortrait);
    },
    completeStep({ commit }, step: string) {
      commit(SET_STEP_COMPLETE, { step, completed: true });
    },
    updateProcessRoutes({ commit, state }) {
      const routes = getPublicRoutes(i18n.locale)
        .filter(
          route =>
            !route.name?.includes(RouteNames.ContactAppointment) &&
            !route.name?.includes(RouteNames.ContactOffer) &&
            !route.name?.includes(RouteNames.ContactConfirmation)
        )
        .map((route, index) => ({
          name: route.children ? route.children[0].name : route.name,
          path: route.path,
          completed: state.processRoutes[index]?.completed ?? false,
          type: route.meta.type
        }));

      commit(SET_PROCESS_ROUTES, routes);
    },
    clearCompletedSteps({ commit, state }) {
      state.processRoutes.forEach(step => {
        commit(SET_STEP_COMPLETE, {
          step: step.name,
          completed: step.name.includes(RouteNames.HouseSize) ? true : false
        });
      });
    },
    updateFontsLoaded({ commit }) {
      commit(SET_FONTS_LOADED);
    },
    setCurrentRoute({ commit }, route: Route) {
      // Clear 'matched' because of circular structure of this array
      commit(SET_CURRENT_ROUTE, { ...route, matched: [] });
    },
    setLoadingState({ commit }, loading: boolean) {
      commit(SET_LOADING_STATE, loading);
    },
    setInitializedState({ commit }) {
      commit(SET_INITIALIZED_STATE);
    },
    setInitialAnimationCompleted({ commit }) {
      commit(SET_INITIAL_ANIMATION_COMPLETED);
    },
    resetState({ commit }) {
      commit(SET_PROCESS_ROUTES, []);
    },
    async updateLocale({ commit, dispatch }, payload: { locale: string; isInitial?: boolean }) {
      commit(SET_LOCALE, payload.locale);

      const currentRoute: RouteNames = router.currentRoute.name?.slice(3) as RouteNames;

      document.documentElement.lang = payload.locale.split('-')[0];
      document.title = i18n.t('global.windowTitle') as string;

      dispatch('updateProcessRoutes');
      dispatch('summary/updateRoutes', null, { root: true });

      if (!payload.isInitial) {
        const newLocalizedRoute = tRoute(currentRoute);

        // check if prev route path is same as target route path (e.g. Home Route)
        if (router.resolve(newLocalizedRoute).href !== router.resolve(router.currentRoute as RawLocation).href) {
          router.replace(newLocalizedRoute);
        }

        localStorage.setItem('locale', payload.locale);

        await dispatch('configuration/setTypes', null, { root: true });
        await dispatch('home/setPresets', null, { root: true });

        if (currentRoute !== RouteNames.Home) {
          await dispatch('configuration/fetchSizes', null, { root: true });

          dispatch('configuration/updateConfiguration', { update: null }, { root: true });
        }
      }
    }
  },
  mutations: {
    [SET_DEVICE_MEDIA_QUERY](state, MQ: string) {
      state.deviceMQ = MQ;
    },
    [SET_IMAGE_MEDIA_QUERY](state, MQ: string) {
      state.imageMQ = MQ;
    },
    [SET_PORTRAIT](state, isPortrait: boolean) {
      state.isPortrait = isPortrait;
    },
    [SET_STEP_COMPLETE](state, { step, completed }: { step: string; completed: boolean }) {
      const completedStep = state.processRoutes.find(route => route.name === step);

      if (completedStep) {
        completedStep.completed = completed;
      }
    },
    [SET_PROCESS_ROUTES](state, routes: Step[]) {
      state.processRoutes = routes;
    },
    [SET_FONTS_LOADED](state) {
      state.fontsLoaded = true;
    },
    [SET_CURRENT_ROUTE](state, route: Route) {
      state.currentRoute = route;
    },
    [SET_LOADING_STATE](state, loading: boolean) {
      state.loading = loading;
    },
    [SET_INITIALIZED_STATE](state) {
      state.initialized = true;
    },
    [SET_INITIAL_ANIMATION_COMPLETED](state) {
      state.initialAnimationCompleted = true;
    },
    [RESET_STATE](state) {
      Object.assign(state, appInitialState());
    },
    [SET_LOCALE](state, locale: string) {
      state.locale = i18n.locale = locale;
    }
  }
};
